import type { CategoryStructure, SubCategory } from "./categoryStructure";
import {
  isDeprecatedAudioSubcategory,
  isDeprecatedFurnitureSubcategory,
  isHiddenFromSellPickerApplianceSubcategory,
  isHiddenFromSellPickerFanSubcategory,
  isHiddenFromSellPickerFridgeSubcategory,
  isHiddenFromSellPickerGasCookerSubcategory,
  isHiddenFromSellPickerKitchenCabinetSubcategory,
  isHiddenFromSellPickerSnookerBoardSubcategory,
} from "./categoryStructure";
import { isHiddenFromSellPickerByListingFamilyRegistry } from "@/lib/listing/listingFamilyRegistry";
import type { ItemCategory } from "./types";

/**
 * True when a subcategory must not appear in buyer/seller catalog pickers
 * (sell, market, request-item, near-me prefs, bare CategoryDropdown).
 * Legacy ids stay in full `categoryStructure` for reads / admin activation.
 */
export function isHiddenFromCatalogPickerSubcategory(
  subcategoryId: string,
): boolean {
  return (
    isDeprecatedFurnitureSubcategory(subcategoryId) ||
    isDeprecatedAudioSubcategory(subcategoryId) ||
    isHiddenFromSellPickerApplianceSubcategory(subcategoryId) ||
    isHiddenFromSellPickerFanSubcategory(subcategoryId) ||
    isHiddenFromSellPickerGasCookerSubcategory(subcategoryId) ||
    isHiddenFromSellPickerFridgeSubcategory(subcategoryId) ||
    isHiddenFromSellPickerKitchenCabinetSubcategory(subcategoryId) ||
    isHiddenFromSellPickerSnookerBoardSubcategory(subcategoryId) ||
    isHiddenFromSellPickerByListingFamilyRegistry(subcategoryId)
  );
}

/**
 * Activation state: sets of enabled category and subcategory ids.
 * Entities not in the set are treated as disabled when building active structure.
 */
export interface CategoryActivation {
  categoryIds: Set<string>;
  subcategoryIds: Set<string>;
}

/**
 * Build activation sets from the public API response (enabled ids only).
 * When building active structure, "enabled" = present in these sets.
 */
export function activationFromApiResponse(response: {
  categories: string[];
  subcategories: string[];
}): CategoryActivation {
  return {
    categoryIds: new Set(response.categories),
    subcategoryIds: new Set(response.subcategories),
  };
}

/**
 * Returns the category structure filtered to only active categories and subcategories.
 * Always drops catalog-picker-hidden (deprecated/legacy) ids.
 */
export function getActiveCategoryStructure(
  fullStructure: CategoryStructure[],
  activation: CategoryActivation,
): CategoryStructure[] {
  const result: CategoryStructure[] = [];

  for (const category of fullStructure) {
    const categoryActive = activation.categoryIds.has(category.id);
    const subList = category.subcategories;

    if (subList.length === 0) {
      if (categoryActive) {
        result.push({ ...category, subcategories: [] });
      }
      continue;
    }

    const activeSubs = subList.filter(
      (sub) =>
        activation.subcategoryIds.has(sub.id) &&
        !isHiddenFromCatalogPickerSubcategory(sub.id),
    );

    if (categoryActive && activeSubs.length > 0) {
      result.push({
        id: category.id as ItemCategory,
        name: category.name,
        subcategories: activeSubs,
      });
    }
  }

  return result;
}

/**
 * Strip catalog-picker-hidden (deprecated/legacy) subcategory ids.
 * Use for any user-facing category picker that is not activation-gated.
 */
export function excludePickerHiddenSubcategories(
  structure: CategoryStructure[],
): CategoryStructure[] {
  return structure.map((category) => ({
    ...category,
    subcategories: category.subcategories.filter(
      (sub) => !isHiddenFromCatalogPickerSubcategory(sub.id),
    ),
  }));
}

/**
 * @deprecated Prefer `excludePickerHiddenSubcategories` — same behavior.
 */
export function excludeDeprecatedFurnitureSubcategories(
  structure: CategoryStructure[],
): CategoryStructure[] {
  return excludePickerHiddenSubcategories(structure);
}

/**
 * Flatten active structure to all subcategories (for search within active only).
 */
export function getActiveSubcategories(
  activeStructure: CategoryStructure[],
): SubCategory[] {
  return activeStructure.flatMap((cat) => cat.subcategories);
}

/**
 * Stable id set for sell/bulk/spec checks. Callers in React should memoize
 * on the structure reference so hook deps do not churn every render.
 */
export function activeSubcategoryIdSet(
  activeStructure: CategoryStructure[],
): Set<string> {
  return new Set(getActiveSubcategories(activeStructure).map((s) => s.id));
}

/**
 * Search subcategories within a category structure (e.g. active structure).
 * Returns subcategories whose name matches the search term (case-insensitive).
 */
export function searchInCategoryStructure(
  structure: CategoryStructure[],
  searchTerm: string,
): SubCategory[] {
  if (!searchTerm.trim()) return [];
  const normalized = searchTerm.toLowerCase();
  const results: SubCategory[] = [];
  for (const category of structure) {
    for (const sub of category.subcategories) {
      if (sub.name.toLowerCase().includes(normalized)) {
        results.push(sub);
      }
    }
  }
  return results;
}
